import { useEffect, useRef, useState } from "react";

/** Touch-friendly number input with minus and plus buttons; counts immediately and reports every step. */
export function Stepper(props: {
  value: number;
  min?: number;
  max?: number;
  label: string;
  disabled?: boolean;
  /** The value can also be typed, e.g. a count of many pieces, where steps of one would take many taps. */
  typed?: boolean;
  /** Shown after the buttons and read with the group, e.g. „von 20“. */
  suffix?: string;
  /** May return a promise; while changes are in flight, intermediate server values do not reset the counter. */
  onChange: (value: number) => unknown;
}) {
  const { min = Number.NEGATIVE_INFINITY, max = Number.POSITIVE_INFINITY } = props;
  const [value, setValue] = useState(props.value);
  // What the user is typing, until the field loses focus; only a whole number within the bounds counts.
  const [draft, setDraft] = useState<string | null>(null);
  const inFlight = useRef(0);
  const [settled, setSettled] = useState(0);
  useEffect(() => {
    if (inFlight.current === 0) setValue(props.value);
  }, [props.value, settled]);
  const change = (next: number) => {
    setValue(next);
    const result = props.onChange(next);
    if (result instanceof Promise) {
      inFlight.current++;
      void result.finally(() => {
        inFlight.current--;
        setSettled((n) => n + 1);
      });
    }
  };
  const step = (next: number) => {
    setDraft(null);
    change(next);
  };
  const type = (raw: string) => {
    setDraft(raw);
    const next = Number(raw);
    if (raw.trim() !== "" && Number.isInteger(next) && next >= min && next <= max && next !== value) change(next);
  };
  return (
    <span className="stepper" role="group" aria-label={props.suffix ? `${props.label} ${props.suffix}` : props.label}>
      <button type="button" className="icon-button" aria-label={`${props.label} verringern`} disabled={props.disabled || value <= min} onClick={() => step(value - 1)}>
        −
      </button>
      {props.typed ? (
        <input
          type="number"
          inputMode="numeric"
          className="stepper-input"
          aria-label={props.label}
          min={Number.isFinite(min) ? min : undefined}
          max={Number.isFinite(max) ? max : undefined}
          step={1}
          disabled={props.disabled}
          value={draft ?? String(value)}
          onChange={(e) => type(e.target.value)}
          onBlur={() => setDraft(null)}
        />
      ) : (
        <span className="stepper-value" aria-live="polite">
          {value}
        </span>
      )}
      <button type="button" className="icon-button" aria-label={`${props.label} erhöhen`} disabled={props.disabled || value >= max} onClick={() => step(value + 1)}>
        +
      </button>
      {props.suffix && <span className="muted small">{props.suffix}</span>}
    </span>
  );
}
